import '@fontsource-variable/space-grotesk';
import '@fontsource/jetbrains-mono/400.css';
import './styles.css';
import { LEVELS, ONSITE_INFO, ONSITE_PLAN, ONSITE_THEMES, SANDBOX, type LevelDef } from './core/levels';
import type { RunReport } from './core/report';
import type { World } from './core/world';
import { Demo } from './game/demo';
import { installApi } from './game/api';
import { loadProgress, loadSettings, recordRun, saveSettings, type Settings } from './game/progress';
import { Session } from './game/session';
import { clear } from './ui/dom';
import { introScreen, levelsScreen, onsiteBreakScreen, onsitePickScreen, onsiteReportScreen, reportScreen, settingsScreen, titleScreen } from './ui/screens';
import { ReplayView } from './ui/replayView';
import { parseRunFile, type RunFile } from './core/replay';
import { exportRun } from './core/report';
import { voiceStatus } from './voice/live';

class App {
  private canvas = document.getElementById('game') as HTMLCanvasElement;
  private hudRoot = document.getElementById('hud') as HTMLElement;
  private screens = document.getElementById('screens') as HTMLElement;
  private session: Session | null = null;
  private demo: Demo | null = null;
  private replayView: ReplayView | null = null;
  private settings: Settings = loadSettings();
  private voice: { gptLive: boolean; reason?: string; passphrase?: boolean } | null = null;

  constructor() {
    this.applySettings();
    installApi(() => this.session);
    // Test hook, ?debug=1 only: screen positions for automated clicking. Any run
    // played with it on says so in its own log, so it can't pass as a clean run.
    if (new URLSearchParams(location.search).get('debug') === '1') {
      (window as unknown as { __brDebug: unknown }).__brDebug = {
        toScreen: (x: number, y: number) => {
          const s = this.session;
          if (!s) return null;
          s.world.apply({ type: 'note', text: 'Debug hook used: this run was driven by a test.' }, 'system');
          const [sx, sy] = s.renderer.cam.toScreen(x + 0.5, y + 0.5);
          return { x: sx, y: sy };
        },
      };
    }
    voiceStatus().then((v) => (this.voice = v)).catch(() => (this.voice = { gptLive: false, reason: 'status check failed' }));
    console.log('%cBlast Radius', 'font: 600 16px sans-serif', '\nThere’s a console in here somewhere. Try blastRadius.help() once a level is running.');
    const level = new URLSearchParams(location.search).get('level');
    const pick = level ? [...LEVELS, SANDBOX].find((l) => l.id === level || String(l.index) === level) : undefined;
    if (pick) this.intro(pick);
    else this.title();
  }

  private applySettings() {
    document.body.classList.toggle('reduced', this.settings.reducedMotion);
    if (this.session) {
      this.session.settings = this.settings;
      this.session.view.reducedMotion = this.settings.reducedMotion;
      this.session.view.showLabels = this.settings.labels;
    }
  }

  private show(el: HTMLElement | null) {
    clear(this.screens);
    if (el) this.screens.append(el);
  }

  private ensureDemo() {
    if (!this.demo && !this.session) this.demo = new Demo(this.canvas, this.settings.reducedMotion);
  }

  private stopDemo() {
    this.demo?.stop();
    this.demo = null;
  }

  private endSession() {
    this.session?.destroy();
    this.session = null;
    this.replayView?.destroy();
    this.replayView = null;
  }

  replay(run: RunFile) {
    this.endSession();
    this.stopDemo();
    this.show(null);
    this.replayView = new ReplayView(run, this.canvas, this.hudRoot, this.settings.reducedMotion, () => {
      this.replayView = null;
      this.title();
    });
  }

  private nextLevel(): LevelDef {
    const p = loadProgress();
    return LEVELS.find((l) => !p[l.id]?.stars[0]) ?? LEVELS[LEVELS.length - 1];
  }

  title() {
    this.endSession();
    this.ensureDemo();
    const next = this.nextLevel();
    this.show(titleScreen({
      nextName: next.name,
      onPlay: () => this.intro(next),
      onLevels: () => this.levels(),
      onSandbox: () => this.intro(SANDBOX),
      onSettings: () => this.openSettings(() => this.title()),
      onWatch: (f) => {
        f.text()
          .then((t) => this.replay(parseRunFile(t)))
          .catch((err: unknown) => alert(err instanceof Error ? err.message : String(err)));
      },
    }));
  }

  levels() {
    this.endSession();
    this.ensureDemo();
    this.show(levelsScreen({ onPick: (l) => this.intro(l), onOnsite: () => this.onsite(), onBack: () => this.title() }));
  }

  /** The onsite: pick a theme, then three problems released in turn at 8, 4 and 2 minutes. */
  onsite() {
    this.endSession();
    this.ensureDemo();
    this.show(onsitePickScreen(ONSITE_THEMES, {
      onBack: () => this.levels(),
      onPick: (id) => {
        const i = ONSITE_THEMES.findIndex((t) => t.id === id);
        const second = ONSITE_THEMES[(i + 1) % ONSITE_THEMES.length];
        const parts: LevelDef[] = [ONSITE_THEMES[i].build(8), second.build(4), ONSITE_PLAN].map((l, k) => ({
          ...l,
          part: { group: 'onsite', index: k + 1, total: 3 },
        }));
        this.onsitePart(parts, 0, []);
      },
    }));
  }

  private onsitePart(parts: LevelDef[], k: number, done: { level: LevelDef; report: RunReport; world: World }[]) {
    this.endSession();
    this.stopDemo();
    const level = parts[k];
    const last = done[done.length - 1];
    this.session = new Session(level, this.canvas, this.hudRoot, this.settings, {
      onEnd: (report, world) => {
        const all = [...done, { level, report, world }];
        if (k + 1 < parts.length) {
          this.onsitePart(parts, k + 1, all);
          return;
        }
        const builds = all.slice(0, 2);
        const stars: [boolean, boolean, boolean] = [true, all[2].world.outcome === 'won', builds.every((p) => p.world.outcome === 'won')];
        const agency = Math.round(all.reduce((sum, p) => sum + p.report.agency, 0) / all.length);
        const safety = Math.round(all.reduce((sum, p) => sum + p.report.safety, 0) / all.length);
        const seconds = all.reduce((sum, p) => sum + p.report.metrics.seconds, 0);
        recordRun(ONSITE_INFO.id, { stars, seconds, agency, safety, won: stars[1] || stars[2] });
        this.show(onsiteReportScreen(all, stars, { onRetry: () => this.onsite(), onLevels: () => this.levels() }));
      },
      onQuit: () => this.levels(),
      onRestart: () => this.onsite(),
      onSettings: () => this.openSettings(() => this.show(null)),
    });
    this.show(onsiteBreakScreen(k + 1, last ? { name: last.level.name, won: last.world.outcome === 'won', reason: last.report.metrics.reason } : null, level, {
      onStart: () => {
        this.show(null);
        this.session?.begin();
      },
    }));
  }

  intro(level: LevelDef) {
    this.endSession();
    this.stopDemo();
    this.session = new Session(level, this.canvas, this.hudRoot, this.settings, {
      onEnd: (report, world) => {
        const idx = LEVELS.findIndex((l) => l.id === level.id);
        const hasNext = idx >= 0 && idx < LEVELS.length - 1;
        this.show(reportScreen(report, world, this.settings.reducedMotion, {
          hasNext,
          onRetry: () => this.intro(level),
          onNext: () => this.intro(LEVELS[idx + 1]),
          onLevels: () => this.levels(),
          onReplay: () => this.replay(JSON.parse(JSON.stringify(exportRun(world, report))) as RunFile),
        }));
      },
      onQuit: () => this.levels(),
      onRestart: () => this.intro(level),
      onSettings: () => this.openSettings(() => this.show(null)),
    });
    const url = new URL(location.href);
    url.searchParams.set('level', level.id);
    history.replaceState(null, '', url);
    this.show(introScreen(level, {
      onStart: () => {
        this.show(null);
        this.session?.begin();
      },
      onBack: () => this.levels(),
    }));
  }

  private openSettings(back: () => void) {
    this.show(settingsScreen(this.settings, this.voice, {
      onChange: (s) => {
        this.settings = s;
        saveSettings(s);
        this.applySettings();
      },
      onBack: back,
    }));
  }
}

new App();
